/**
 * Rutas de accounting — contabilidad: hub /accounting con sus lentes, cartera y Portal del Asesor.
 *
 * Se componen en `src/pages/index.jsx`. Es un fragmento, no un array: React Router
 * recorre los `<React.Fragment>` hijos de `<Routes>`, así que el JSX se mudó tal cual
 * —sin `key` ni comas— y el diff es el movimiento, nada más.
 *
 * `routeTable.test.jsx` congela las 174 rutas de la app contra la tabla previa a la
 * mudanza: perder una ruta aquí no da error, da un 404.
 */
import { Route, Navigate } from 'react-router-dom';
import AccountingRedirect from './AccountingRedirect';
import { lazyWithRetry } from '@/lib/lazyWithRetry';
import { PermissionGuard } from '@/components/auth/PermissionGuard';
import { CcaaTabRedirect, FirmRouteGate, MigrationRedirect } from '@/pages/routeHelpers';

const ContabilidadRedirect = lazyWithRetry(() => import("@/pages/ContabilidadRedirect"));
const Cartera = lazyWithRetry(() => import("@/pages/Cartera"));
const RevenueUnitDetail = lazyWithRetry(() => import("@/pages/RevenueUnitDetail"));
const AccountingHub = lazyWithRetry(() => import("@/pages/AccountingHub"));
const AccountingIndex = lazyWithRetry(() => import("@/pages/accounting/AccountingIndex"));
const AccountingJournal = lazyWithRetry(() => import("@/pages/accounting/JournalIndex"));
const AccountingLedger = lazyWithRetry(() => import("@/pages/accounting/LedgerIndex"));
const AccountingTrialBalance = lazyWithRetry(() => import("@/pages/accounting/TrialBalance"));
const AccountingBalanceSheet = lazyWithRetry(() => import("@/pages/accounting/BalanceSheet"));
const AccountingRecurringJournalEntries = lazyWithRetry(() => import("@/pages/accounting/RecurringJournalEntries"));
const AccountingLibrosRegistroIVA = lazyWithRetry(() => import("@/pages/accounting/LibrosRegistroIVAIndex"));
const AccountingLibrosIrpfHub = lazyWithRetry(() => import("@/pages/accounting/LibrosIrpfHub"));
const AccountingAnnualAccounts = lazyWithRetry(() => import("@/pages/accounting/AnnualAccounts"));
const AccountingEquity = lazyWithRetry(() => import("@/pages/accounting/EquityAndProvisions"));
const AccountingPeriodifications = lazyWithRetry(() => import("@/pages/accounting/PeriodificationsIndex"));
const AccountingAdvances = lazyWithRetry(() => import("@/pages/accounting/Advances"));
const AccountingFiscalCalendar = lazyWithRetry(() => import("@/pages/accounting/FiscalCalendar"));
const AccountingFixedAssets = lazyWithRetry(() => import("@/pages/accounting/FixedAssets"));
const AccountingChartOfAccounts = lazyWithRetry(() => import("@/pages/accounting/ChartOfAccountsIndex"));
const AdvisorAuxiliares = lazyWithRetry(() => import("@/pages/accounting/AdvisorAuxiliares"));
const AdvisorCostCenters = lazyWithRetry(() => import("@/pages/accounting/AdvisorCostCenters"));
const AccountingClosing = lazyWithRetry(() => import("@/pages/accounting/ClosingIndex"));
const AccountingReports = lazyWithRetry(() => import("@/pages/accounting/Reports"));
const AccountingIncidents = lazyWithRetry(() => import("@/pages/accounting/Incidents"));
const AccountingReconciliation = lazyWithRetry(() => import("@/pages/accounting/Reconciliation"));
const AccountingPunteo = lazyWithRetry(() => import("@/pages/accounting/PunteoContable"));
const AccountingTreasuryCockpit = lazyWithRetry(() => import("@/pages/accounting/TesoreriaCockpit"));
const AccountingSsotHealth = lazyWithRetry(() => import("@/pages/accounting/SsotHealth"));
const AccountingProfitLoss = lazyWithRetry(() => import("@/pages/accounting/ProfitLoss"));
const AccountingAutopilot = lazyWithRetry(() => import("@/pages/accounting/AriaActividad"));
const AccountingAuditTrail = lazyWithRetry(() => import("@/pages/accounting/AuditTrail"));
const AccountingSupportingDocs = lazyWithRetry(() => import("@/pages/accounting/SupportingDocs"));
const PortalAsesor = lazyWithRetry(() => import("@/pages/PortalAsesor"));
const PortalAsesorConfig = lazyWithRetry(() => import("@/pages/PortalAsesorConfig"));
const AsesorCalendar = lazyWithRetry(() => import("@/pages/AsesorCalendar"));
const FirmModelsPage = lazyWithRetry(() => import("@/pages/FirmModelsPage"));
const FirmWowScreen = lazyWithRetry(() => import("@/pages/FirmWowScreen"));
const FirmBatchResultPage = lazyWithRetry(() => import("@/pages/FirmBatchResultPage"));
const G601UploadResponsePage = lazyWithRetry(() => import("@/pages/G601UploadResponsePage"));
const FirmDocsPage = lazyWithRetry(() => import("@/pages/FirmDocsPage"));
const FirmNotificationsPage = lazyWithRetry(() => import("@/pages/FirmNotificationsPage"));
const FirmFeesPage = lazyWithRetry(() => import("@/pages/FirmFeesPage"));
const FirmReportsPage = lazyWithRetry(() => import("@/pages/FirmReportsPage"));
const FirmTeamPage = lazyWithRetry(() => import("@/pages/FirmTeamPage"));
const AsesoriaConfig = lazyWithRetry(() => import("@/pages/AsesoriaConfig"));
const Informes = lazyWithRetry(() => import("@/pages/Informes"));

export const routes = (
  <>

                {/* FASE 5 Sub-PR 5.2 — Portal del Asesor.
                    Guard interno: `useCanSeePortalAsesor` redirige a / si el
                    user no es advisor (asesor.view) ni owner/admin multi-empresa.
                    No usamos `<PermissionGuard permission={PERMS.ASESOR_VIEW}>`
                    porque dejaría fuera a los owners multi-empresa sin ese
                    permiso (caso "CEO con varias sociedades"). */}
                {/* Modo Asesor v2 — ecosistema de despacho. El chrome (cabecera
                    de despacho + tabbar) lo monta el dispatcher de <Layout>
                    (`isFirmEcosystem`); aquí solo va el gate de acceso
                    (FirmRouteGate → Outlet). Los flujos inmersivos (presentando /
                    batch result / upload-response) quedan FUERA, a pantalla
                    completa (rutas top-level más abajo). */}
                {['/portal-asesor', '/mis-empresas'].map(basePath => (
                  <Route key={basePath} path={basePath} element={<FirmRouteGate />}>
                    <Route index element={<PortalAsesor />} />
                    <Route path="configuracion" element={<PortalAsesorConfig />} />
                    <Route path="calendario" element={<AsesorCalendar />} />
                    <Route path="modelos" element={<FirmModelsPage />} />
                    <Route path="documentacion" element={<FirmDocsPage />} />
                    <Route path="notificaciones" element={<FirmNotificationsPage />} />
                    <Route path="honorarios" element={<FirmFeesPage />} />
                    <Route path="equipo" element={<FirmTeamPage />} />
                    <Route path="informes" element={<FirmReportsPage />} />
                  </Route>
                ))}


                {/* Sprint 5.bis.4 — Wow (presentación en curso) + Result screen for bulk AEAT submit. */}
                {['/portal-asesor', '/mis-empresas'].map(basePath => (
                  <Route key={`${basePath}-wow`} path={`${basePath}/presentando/:batchId`} element={<FirmWowScreen />} />
                ))}

                {['/portal-asesor', '/mis-empresas'].map(basePath => (
                  <Route key={`${basePath}-batch`} path={`${basePath}/batch/:batchId`} element={<FirmBatchResultPage />} />
                ))}


                {/* Fase E G601 — wizard upload-response asesor (último paso post AEAT portal + Autofirma). */}
                {['/portal-asesor', '/mis-empresas'].map(basePath => (
                  <Route key={`${basePath}-up`} path={`${basePath}/batch/:batchId/upload-response`} element={<G601UploadResponsePage />} />
                ))}


                {/* Sprint 5.bis.0.4 — config de asesoría por company. Owner/admin
                    only. El gate inline en AsesoriaConfig redirige a "/" si el
                    user.role no es owner ni admin (consistente con el patrón
                    usado en EmployeeDetail). El BE refuerza con
                    requireRole('admin', 'owner') en el router de companies. */}
                <Route path="/configuracion/asesoria" element={<AsesoriaConfig />} />


                {/* Finanzas & Contabilidad */}
                {/* PR14 — legacy retirada; redirect estable para bookmarks (mapea ?tab= → lente del hub). */}
                <Route path="/contabilidad" element={<ContabilidadRedirect />} />

                {/* Cartera — P&L por unidad de ingreso recurrente (Fase D). */}
                <Route path="/cartera" element={<PermissionGuard permission="accounting.view" fallback={<Navigate to="/" replace />}><Cartera /></PermissionGuard>} />

                <Route path="/cartera-detalle" element={<PermissionGuard permission="accounting.view" fallback={<Navigate to="/" replace />}><RevenueUnitDetail /></PermissionGuard>} />

                {/* Accounting hub (FASE 1 PR 1.4) — nested routes con Outlet.
                    Desde PR14 es la ÚNICA superficie de Contabilidad
                    (la legacy `/contabilidad` redirige aquí). */}
                <Route path="/accounting" element={<PermissionGuard permission="accounting.view" fallback={<Navigate to="/" replace />}><AccountingHub /></PermissionGuard>}>
                    <Route index element={<AccountingIndex />} />
                    <Route path="journal" element={<AccountingJournal />} />
                    {/* Asientos manuales absorbido en el Libro Diario (que ya monta
                        AsientoContableForm con su botón "Nuevo asiento"). */}
                    <Route path="manual-entries" element={<AccountingRedirect to="/accounting/journal" />} />
                    <Route path="inventory-books" element={<CcaaTabRedirect tab="inventarios" />} />
                    <Route path="auxiliary-accounts" element={<AdvisorAuxiliares />} />
                    <Route path="cost-centers" element={<AdvisorCostCenters />} />
                    <Route path="ledger" element={<AccountingLedger />} />
                    <Route path="trial-balance" element={<AccountingTrialBalance />} />
                    <Route path="balance-sheet" element={<AccountingBalanceSheet />} />
                    {/* Rediseño 360 — lente Pérdidas y Ganancias (motor SQL /api/reports/profit-loss). */}
                    <Route path="profit-loss" element={<AccountingProfitLoss />} />
                    <Route path="recurring-templates" element={<AccountingRecurringJournalEntries />} />
                    <Route path="libros-registro-iva" element={<AccountingLibrosRegistroIVA />} />
                    {/* agentic-erp#925 — libros IRPF por actividad + catálogo. */}
                    <Route path="libros-irpf" element={<AccountingLibrosIrpfHub />} />
                    <Route path="economic-activities" element={<AccountingRedirect to="/accounting/libros-irpf?tab=actividades" />} />
                    {/* Cuentas Anuales — lente fusionada (Depósito + ECPN + Memoria + Inventarios + XBRL). */}
                    <Route path="cuentas-anuales" element={<AccountingAnnualAccounts />} />
                    <Route path="ecpn" element={<CcaaTabRedirect tab="ecpn" />} />
                    <Route path="memoria" element={<CcaaTabRedirect tab="memoria" />} />
                    <Route path="xbrl" element={<CcaaTabRedirect tab="xbrl" />} />
                    {/* Patrimonio y provisiones — lente fusionada (EquityService). */}
                    <Route path="equity" element={<AccountingEquity />} />
                    <Route path="liability-provisions" element={<AccountingRedirect to="/accounting/equity?tab=provisiones" />} />
                    <Route path="periodifications" element={<AccountingPeriodifications />} />
                    <Route path="capital-increases" element={<AccountingRedirect to="/accounting/equity?tab=ampliaciones" />} />
                    <Route path="dividend-distributions" element={<AccountingRedirect to="/accounting/equity?tab=dividendos" />} />
                    {/* Anticipos — lente fusionada (AdvancesService). */}
                    <Route path="advances" element={<AccountingAdvances />} />
                    <Route path="customer-advances" element={<AccountingRedirect to="/accounting/advances?tab=clientes" />} />
                    <Route path="supplier-advances" element={<AccountingRedirect to="/accounting/advances?tab=proveedores" />} />
                    {/* Inmovilizado — lente fusionada (fixed_assets por asset_class + leasing). */}
                    <Route path="fixed-assets" element={<AccountingFixedAssets />} />
                    <Route path="intangibles" element={<AccountingRedirect to="/accounting/fixed-assets?tab=intangible" />} />
                    <Route path="material-assets" element={<AccountingRedirect to="/accounting/fixed-assets?tab=material" />} />
                    <Route path="leasing-contracts" element={<AccountingRedirect to="/accounting/fixed-assets?tab=leasing" />} />
                    <Route path="tax-prepayments" element={<AccountingRedirect to="/accounting/taxes?tab=pagos" />} />
                    <Route path="chart-of-accounts" element={<AccountingChartOfAccounts />} />
                    {/* Migración movida a /datos (fuera del hub contable). Redirect
                        estable para bookmarks y deep-links `?slice=` antiguos. */}
                    <Route path="import" element={<MigrationRedirect />} />
                    <Route path="closing" element={<AccountingClosing />} />
                    {/* Fase 2 rediseño conta — lente "Actividad de ARIA": torre de control
                        de autonomía (parte diario firmable + dial + feed con Deshacer). */}
                    <Route path="autopilot" element={<AccountingAutopilot />} />
                    <Route path="reports" element={<AccountingReports />} />
                    <Route path="incidents" element={<AccountingIncidents />} />
                    <Route path="reconciliation" element={<AccountingReconciliation />} />
                    <Route path="punteo" element={<AccountingPunteo />} />
                    <Route path="treasury-cockpit" element={<AccountingTreasuryCockpit />} />
                    <Route path="ssot-health" element={<AccountingSsotHealth />} />
                    {/* Rediseño 360 Capa 2 — lentes reales (reemplazan ComingSoonLens). */}
                    <Route path="audit-trail" element={<PermissionGuard permission="accounting.view" fallback={<Navigate to="/accounting" replace />}><AccountingAuditTrail /></PermissionGuard>} />
                    <Route path="supporting-docs" element={<PermissionGuard permission="accounting.view" fallback={<Navigate to="/accounting" replace />}><AccountingSupportingDocs /></PermissionGuard>} />
                    <Route path="taxes" element={<PermissionGuard permission="accounting.view" fallback={<Navigate to="/accounting" replace />}><AccountingFiscalCalendar /></PermissionGuard>} />
                </Route>

                <Route path="/informes" element={<Informes />} />
  </>
);
